import { z } from "zod"

import { MONTHS } from "../time"

export const brochureYearSchema = z
  .object({
    production_kWh: z.number(),
    export_kWh: z.number(),
    import_kWh: z.number(),
    productionCumulative_kWh: z.number(),
    savings_USD: z.number(),
    savingsCumulative_USD: z.number(),
    preSolarUtilityCost_USD: z.number(),
    preSolarRate_USD_PER_kWh: z.number(),
    preSolarUtilityCostMonthly_USD: z.number(),
    preSolarEnergyCostMonthly_USD: z.number(),
    preSolarFixedCostMonthly_USD: z.number(),
    postSolarUtilityCost_USD: z.number(),
    postSolarRate_USD_PER_kWh: z.number(),
    postSolarUtilityCostMonthly_USD: z.number(),
    postSolarEnergyCostMonthly_USD: z.number(),
    postSolarFixedCostMonthly_USD: z.number(),
    postSolarLoanPaymentMonthly_USD: z.number(),
  })
  .strict()

export const brochureYearsSchema = z
  .array(brochureYearSchema)
  .refine((years) => years.length === 0 || years.length === 25, {
    message: `years must contain exactly 0 or 25 elements`,
  })

export const brochureMonthSchema = z
  .object({
    month: z.enum(MONTHS),
    production_kWh: z.number(),
    export_kWh: z.number(),
    import_kWh: z.number(),
    preSolarUtilityCost_USD: z.number(),
    preSolarEnergyCost_USD: z.number(),
    preSolarFixedCost_USD: z.number(),
    postSolarUtilityCost_USD: z.number(),
    postSolarEnergyCost_USD: z.number(),
    postSolarFixedCost_USD: z.number(),
    postSolarLoanPayment_USD: z.number(),
  })
  .strict()

export const brochureMonthsSchema = z.array(brochureMonthSchema).length(12)

export type BrochureYearValues = z.infer<typeof brochureYearSchema>
export type BrochureMonthValues = z.infer<typeof brochureMonthSchema>

export function readBrochureYears(brochure: {
  years: unknown
}): BrochureYearValues[] | null {
  if (brochure.years === null) return null

  return brochureYearsSchema.parse(brochure.years)
}

export function readBrochureMonths(brochure: {
  months: unknown
}): BrochureMonthValues[] | null {
  if (brochure.months === null) return null

  return brochureMonthsSchema.parse(brochure.months)
}

const YEAR_VALUE_KEYS = [
  `production_kWh`,
  `export_kWh`,
  `import_kWh`,
  `productionCumulative_kWh`,
  `savings_USD`,
  `savingsCumulative_USD`,
  `preSolarUtilityCost_USD`,
  `preSolarRate_USD_PER_kWh`,
  `preSolarUtilityCostMonthly_USD`,
  `preSolarEnergyCostMonthly_USD`,
  `preSolarFixedCostMonthly_USD`,
  `postSolarUtilityCost_USD`,
  `postSolarRate_USD_PER_kWh`,
  `postSolarUtilityCostMonthly_USD`,
  `postSolarEnergyCostMonthly_USD`,
  `postSolarFixedCostMonthly_USD`,
  `postSolarLoanPaymentMonthly_USD`,
] as const

const MONTH_VALUE_KEYS = [
  `month`,
  `production_kWh`,
  `export_kWh`,
  `import_kWh`,
  `preSolarUtilityCost_USD`,
  `preSolarEnergyCost_USD`,
  `preSolarFixedCost_USD`,
  `postSolarUtilityCost_USD`,
  `postSolarEnergyCost_USD`,
  `postSolarFixedCost_USD`,
  `postSolarLoanPayment_USD`,
] as const

const pickYearValues = (row: Record<string, unknown>): unknown => ({
  ...Object.fromEntries(YEAR_VALUE_KEYS.map((key) => [key, row[key]])),
  postSolarLoanPaymentMonthly_USD: row.postSolarLoanPaymentMonthly_USD ?? 0,
})

const pickMonthValues = (row: Record<string, unknown>): unknown => ({
  ...Object.fromEntries(MONTH_VALUE_KEYS.map((key) => [key, row[key]])),
  postSolarLoanPayment_USD: row.postSolarLoanPayment_USD ?? 0,
})

export function brochureYearsFromRows(rows: unknown[]): BrochureYearValues[] {
  return brochureYearsSchema.parse(
    rows.map((row) => pickYearValues(row as Record<string, unknown>)),
  )
}

export function brochureMonthsFromRows(rows: unknown[]): BrochureMonthValues[] {
  return brochureMonthsSchema.parse(
    rows.map((row) => pickMonthValues(row as Record<string, unknown>)),
  )
}
